<script setup lang="ts">
import type { EngagementOptionEnumType, LikertScaleEnumType } from '@forgd/shared/enums'
import { useTokenomicsUrl } from '#core/composables/useTokenomicsUrl'
import { formatLiquidityDistribution, isRfqV2Form } from '@forgd/liquidity.forgd.com/layers/rfq/utils/rfq-form-version'
import { countries } from '@forgd/shared/data/countries'
import { getBaseLayerLogos, MarketMakersInterestedInPartneringWithOptions, MMRFQEngagementOptions, MMRFQExternalCapitalOptions, MMRFQMotivationsOptions, MMRFQStableCoinBudgetToAllocateOptions } from '@forgd/shared/dropdowns'
import { LikertScaleEnum } from '@forgd/shared/enums'
import type { BadgeVariants } from '@forgd/ui-kit/badge'
import { MMRFQLikertScaleLabels } from '@forgd/shared/labels'
import { formatValue } from '@forgd/formatter'
import { Badge } from '@forgd/ui-kit/badge'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { BaseLayer } from '@forgd/ui-kit/blocks/base-layer'
import { LoadError } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Sheet, SheetBody, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@forgd/ui-kit/sheet'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { ArrowUpRightIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

interface PreferenceItem {
  label: string
  value: string | number | null | undefined
  hidden?: boolean | null
}

interface PreferenceSection {
  title: string
  preferences: PreferenceItem[]
}

const props = defineProps<{
  rfqId: string | null
}>()

const { model: isOpen } = useAppSlideover('rfq-preferences')

onUnmounted(() => {
  isOpen.value = false
})

function getBeliefsScaleLabel(value: number | null | undefined): string {
  const defaultLabel = MMRFQLikertScaleLabels[LikertScaleEnum.Neutral]
  if (!value) {
    return defaultLabel
  }

  // we store those values as numbers in db, cast type here so we can get the label
  return MMRFQLikertScaleLabels[value as LikertScaleEnumType] ?? defaultLabel
}

function getBeliefsColor(value: number | null | undefined): BadgeVariants['variant'] {
  if (!value || value === LikertScaleEnum.Neutral) {
    return 'yellow'
  } // Neutral
  if (value <= LikertScaleEnum.Disagree) {
    return 'red'
  } // Disagree / Strongly Disagree
  return 'green' // Agree / Strongly Agree
}

const { data, isPending, error } = useQuery({
  ...buildApiQuery('/rfq/mm/rfq-details', {
    params: () => ({
      query: {
        id: String(props.rfqId),
      },
    }),
  }),
  enabled: () => Boolean(props.rfqId),
})

const { getCoingeckoUrl, getTdUrl } = useTokenomicsUrl()
const auth = useAuth()

const project = computed(() => data.value?.project)
const rfq = computed(() => data.value?.rfq)
const baseLayerLogos = computed(() => getBaseLayerLogos(project.value?.baseLayer ?? []))

// v2-form RFQs store hard-coded defaults for the questions the form no longer asks,
// so those rows are hidden instead of shown as if the project answered them
const isRfqV2 = computed(() => isRfqV2Form(rfq.value))

const btnNavigateTo = computed(() => {
  if (!project.value || Object.keys(project.value).length === 0) {
    return { title: '', url: '' }
  }

  if (project.value.coingeckoId) {
    return {
      title: 'CoinGecko Profile',
      url: getCoingeckoUrl(project.value.coingeckoId),
    }
  }

  const { isTdPublished, ticker } = project.value
  const shareTdPublishedPage = rfq.value?.shareTdPublishedPage ?? null

  const disabled = !ticker || !isTdPublished || (auth.isMarketMaker && shareTdPublishedPage === false)
  const url = disabled ? '' : getTdUrl(project.value.id, project.value.ticker)

  return { title: 'Token Public Page', url, disabled }
})

const tokenomicsSource = computed(() => {
  if (data.value?.tokenDesignerDetails) {
    return {
      maxTokenSupply: data.value.tokenDesignerDetails.maxTokenSupply,
      circulatingSupply: data.value.tokenDesignerDetails.circulatingSupply,
      price: data.value.tokenDesignerDetails.tokenPriceAtTGE,
      marketCap: data.value.tokenDesignerDetails.marketCapAtTge,
      fdv: data.value.tokenDesignerDetails.fdvAtTge,
    }
  }

  if (data.value?.tokenMarketDetails) {
    return {
      maxTokenSupply: data.value.tokenMarketDetails.maxTokenSupply,
      circulatingSupply: data.value.tokenMarketDetails.circulatingSupply,
      price: data.value.tokenMarketDetails.price,
      marketCap: data.value.tokenMarketDetails.marketCap,
      fdv: data.value.tokenMarketDetails.fdv,
    }
  }

  return null
})

const standardSections = computed<PreferenceSection[]>(() => {
  const tokenomics = tokenomicsSource.value
  const isListed = project.value?.coingeckoId

  return [
    {
      title: 'Tokenomics Details',
      preferences: [
        { label: 'Est. Date of TGE', value: formatValue(project.value?.tgeDate, 'date'), hidden: !project.value || project.value.tokenListed },
        { label: isListed ? 'Circulating Token Supply' : 'Circulating Token Supply at TGE', value: formatValue(tokenomics?.circulatingSupply, 'number') },
        { label: 'Total Token Supply', value: formatValue(tokenomics?.maxTokenSupply, 'number') },
        { label: isListed ? 'Token Price' : 'Est. Token Price at TGE', value: formatValue(tokenomics?.price, 'currency') },
        { label: 'Market Capitalization ("MC")', value: formatValue(tokenomics?.marketCap, 'currency') },
        { label: 'Fully Diluted Valuation ("FDV")', value: formatValue(tokenomics?.fdv, 'currency') },
      ],
    },
    {
      title: 'RFQ Preferences',
      preferences: [
        { label: 'Desired Number of Market Makers', value: MarketMakersInterestedInPartneringWithOptions.find(m => m.value === rfq.value?.marketMakersQty)?.label },
        { label: 'Needs CEX or DEX specialists', value: rfq.value?.hasExchangeSpecialists ? 'Yes' : 'No', hidden: isRfqV2.value },
        { label: 'Desired Number of Specialists', value: rfq.value ? `CEX: ${rfq.value.specialistsCEX} | DEX: ${rfq.value.specialistsDEX}` : undefined, hidden: isRfqV2.value },
        { label: 'Preferred Business Model', value: MMRFQEngagementOptions.filter(o => (rfq.value?.engagementOptions || []).includes(o.value as EngagementOptionEnumType)).map(o => o.label).join(', ') },
        { label: 'Est. Engagements Token Allocation', value: rfq.value?.tokenSupplyPercentageToAllocate },
        { label: 'Est. Engagement Stablecoin Budget', value: MMRFQStableCoinBudgetToAllocateOptions.find(i => i.value === rfq.value?.stableCoinBudgetToAllocate)?.label },
        { label: 'Priorities & Motivations', value: MMRFQMotivationsOptions.filter(i => rfq.value?.motivations?.includes(i.value)).map(o => o.label).join(', '), hidden: isRfqV2.value },
      ],
    },
    {
      title: 'Additional Details',
      preferences: [
        { label: isRfqV2.value ? 'CEXs with Listing Confirmation' : 'Desired CEXs for TGE', value: rfq.value?.exchangesCEX?.join(', ') },
        { label: isRfqV2.value ? 'DEXs with Listing Confirmation' : 'Desired DEXs for TGE', value: rfq.value?.exchangesDEX?.join(', '), hidden: !isRfqV2.value && (!project.value || project.value.tokenListed) },
        { label: 'Liquidity Distribution', value: formatLiquidityDistribution(rfq.value), hidden: !isRfqV2.value },
        { label: 'Requests Support on Exchanges', value: rfq.value?.hasSupportExchanges ? 'Yes' : 'No', hidden: isRfqV2.value },
        { label: 'Required CEXs', value: rfq.value?.supportExchangesCEX?.join(', '), hidden: isRfqV2.value },
        { label: 'Required DEXs', value: rfq.value?.supportExchangesDEX?.join(', '), hidden: isRfqV2.value },
        { label: 'External Capital Raised to Date', value: MMRFQExternalCapitalOptions.find(o => o.value === rfq.value?.externalCapital)?.label, hidden: isRfqV2.value },
        { label: 'Notable Investors', value: rfq.value?.investors?.join(', '), hidden: isRfqV2.value },
        { label: 'Jurisdiction of Token Issuing Entity', value: rfq.value?.country === 'i_dont_know' ? 'I do not know' : countries.find(c => c.value === rfq.value?.country)?.label, hidden: isRfqV2.value },
        { label: 'Has Legal Opinion?', value: rfq.value?.hasLegalOpinionOnUtilityStatus ? 'Yes' : 'No', hidden: isRfqV2.value },
      ],
    },
  ]
})

const beliefsSection = computed(() => [
  { label: 'I want to work with the best market maker(s), even if it means accepting a proposal with less favorable terms.', value: rfq.value?.preferMarketMakerQuality },
  { label: 'I want my market maker(s) to provide as much liquidity as possible, even if it means extending a larger token loan.', value: rfq.value?.preferLiquidityDepth },
  { label: 'I\'m sensitive to the prices at which my market maker(s) will be able to purchase the tokens that I loan them via options.', value: rfq.value?.preferTokenPurchasePrice },
  { label: 'I want to minimize the amount of stablecoins allocated to my market makers, even if it may negatively impacts my token\'s price performance.', value: rfq.value?.preferStablecoinEfficiency },
])
</script>

<template>
  <Sheet :open="isOpen" @update:open="(open) => !open && (isOpen = false)">
    <SheetContent
      side="right"
      size="md"
      data-ui="Sheet"
    >
      <SheetHeader>
        <div>
          <SheetTitle class="text-sm font-semibold">
            All RFQ Preferences
          </SheetTitle>
          <SheetDescription>
            The following list shows all RFQ preferences the project described on the application.
          </SheetDescription>
        </div>
        <SheetClose as-child>
          <Button type="button" size="sm" variant="ghost" icon-only aria-label="Close" data-testid="close-slideover-button">
            <XMarkIcon />
          </Button>
        </SheetClose>
      </SheetHeader>

      <SheetBody>
        <LoadError
          v-if="error"
          title="Unable to load RFQ preferences"
          message="Something went wrong while loading the data. Please try again later."
        />

        <div v-else class="flex flex-col gap-5">
          <!-- Project Header -->
          <div class="flex flex-col gap-5 py-5">
            <div class="flex items-center gap-2">
              <template v-if="isPending">
                <Skeleton class="size-6" />
                <Skeleton class="h-4 w-12" />
                <Skeleton class="h-3 w-24" />
              </template>
              <template v-else>
                <ProjectAvatar :src="project?.image" size="xs" />
                <div class="font-semibold text-primary-900">
                  {{ project?.ticker }}
                </div>
                <div class="text-xs text-gray-600">
                  {{ project?.name }}
                </div>
              </template>
            </div>

            <!-- Listing Status & Base Layer -->
            <div class="flex items-center justify-between flex-wrap">
              <div class="flex gap-1">
                <template v-if="isPending">
                  <Skeleton class="h-8 w-20" />
                  <Skeleton class="h-8 w-40" />
                </template>
                <template v-else>
                  <Badge size="md" variant="gray">
                    {{ project?.tokenListed ? 'Listed' : 'Pre-TGE' }}
                  </Badge>
                  <BaseLayer :layers="baseLayerLogos" class="h-8" />
                </template>
              </div>
              <template v-if="!isPending">
                <Button v-if="btnNavigateTo.url" as-child variant="outline" size="sm">
                  <NuxtLink :to="btnNavigateTo.url">
                    <img v-if="project?.coingeckoId" src="/coingecko.svg" class="size-3 rounded-full" alt="">
                    <GlobeAltIcon v-else aria-hidden="true" />
                    {{ btnNavigateTo.title }}
                    <ArrowUpRightIcon aria-hidden="true" />
                  </NuxtLink>
                </Button>
                <Button v-else variant="outline" size="sm" disabled>
                  <img v-if="project?.coingeckoId" src="/coingecko.svg" class="size-3 rounded-full" alt="">
                  <GlobeAltIcon v-else aria-hidden="true" />
                  {{ btnNavigateTo.title }}
                  <ArrowUpRightIcon aria-hidden="true" />
                </Button>
              </template>
            </div>
          </div>

          <!-- Standard Sections (Tokenomics, RFQ Preferences, Additional Details) -->
          <Card v-for="section in standardSections" :key="section.title">
            <CardHeader class="min-h-0 p-4">
              <CardTitle>{{ section.title }}</CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col p-0 py-2">
              <template v-for="preference in section.preferences" :key="preference.label">
                <div v-if="!preference.hidden" class="grid grid-cols-2 gap-2 items-center p-2 min-h-[56px]">
                  <template v-if="isPending">
                    <Skeleton class="h-[17px] my-2.5 w-full" />
                    <Skeleton class="h-[17px] my-2.5 w-full" />
                  </template>
                  <template v-else>
                    <span class="text-sm text-secondary px-2">{{ preference.label }}</span>
                    <div class="bg-neutral-200 p-2 rounded-lg text-sm text-primary-900 min-h-9 h-full flex items-center">
                      {{ preference.value || 'N/A' }}
                    </div>
                  </template>
                </div>
              </template>
            </CardContent>
          </Card>

          <!-- Market Maker Engagements Beliefs (not asked by the v2 form) -->
          <Card v-if="!isRfqV2">
            <CardHeader class="min-h-0 p-4">
              <CardTitle>Market Maker Engagements Beliefs</CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col gap-4">
              <template v-if="isPending">
                <div v-for="i in 4" :key="i" class="flex flex-col gap-2">
                  <Skeleton class="h-[17px] w-full" />
                  <Skeleton class="h-6 w-24" />
                </div>
              </template>
              <template v-else>
                <div v-for="belief in beliefsSection" :key="belief.label" class="flex flex-col gap-2">
                  <span class="text-sm text-secondary">{{ belief.label }}</span>
                  <div>
                    <Badge size="md" :variant="getBeliefsColor(belief.value)">
                      {{ getBeliefsScaleLabel(belief.value) }}
                    </Badge>
                  </div>
                </div>
              </template>
            </CardContent>
          </Card>
        </div>
      </SheetBody>
    </SheetContent>
  </Sheet>
</template>
